import { useEffect, useState } from 'react';
import type { EarthingSystem, InstallationUsage, ProjectMeta, Standard } from '@elec/domain';
import { LOCALES, type MessageKey, type Translate } from '@elec/i18n';
import { Dialog } from './ui.tsx';

const STANDARD_VALUES: Standard[] = ['rgie', 'nfc15100', 'nen1010'];

const TEXT_FIELDS: Array<keyof ProjectMeta> = [
  'title',
  'ownerName',
  'ownerAddress',
  'ownerEmail',
  'ownerPhone',
  'installationAddress',
  'author',
  'company',
];

/** The input of the first field R-DOC-02 reports missing, in the order of the form. */
export function firstMissingField(m: ProjectMeta): string | null {
  if (!m.ownerName) return 'm-ownerName';
  if (!m.ownerAddress && !m.installationAddress) return 'm-ownerAddress';
  if (!m.author && !m.company) return 'm-author';
  if (!m.date) return 'm-date';
  if (!m.supplyVoltage) return 'm-supply';
  return null;
}

export function ProjectInfoDialog(props: {
  meta: ProjectMeta;
  t: Translate;
  onSave(patch: Partial<ProjectMeta>): void;
  onClose(): void;
  /** Put the cursor in the first title-block field that is still empty (from the checks). */
  focusMissing?: boolean;
}) {
  const { t } = props;
  const [m, setM] = useState<ProjectMeta>(props.meta);
  useEffect(() => {
    if (!props.focusMissing) return;
    const id = firstMissingField(props.meta);
    if (id) document.getElementById(id)?.focus();
    // On opening only.
  }, []);
  const set = <K extends keyof ProjectMeta>(k: K, v: ProjectMeta[K]) => setM((o) => ({ ...o, [k]: v }));
  const save = () => {
    const patch: Partial<ProjectMeta> = {};
    for (const k of Object.keys(m) as Array<keyof ProjectMeta>)
      if (m[k] !== props.meta[k]) (patch as Record<string, unknown>)[k] = m[k];
    if (Object.keys(patch).length) props.onSave(patch);
    props.onClose();
  };
  return (
    <Dialog
      title={t('menu.projectInfo')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.onClose}>{t('dialog.cancel')}</button>
          <button className="primary" onClick={save} data-testid="project-info-save">
            {t('dialog.ok')}
          </button>
        </>
      }
    >
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        {TEXT_FIELDS.map((k) => (
          <div className="field" key={k}>
            <label htmlFor={`m-${k}`}>{t(`meta.${k}` as MessageKey)}</label>
            <input
              id={`m-${k}`}
              type={k === 'ownerEmail' ? 'email' : k === 'ownerPhone' ? 'tel' : 'text'}
              value={String(m[k] ?? '')}
              onChange={(e) => set(k, e.target.value as never)}
            />
          </div>
        ))}
        <div className="field">
          <label htmlFor="m-date">{t('meta.date')}</label>
          <input id="m-date" type="date" value={m.date} onChange={(e) => set('date', e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="m-supply">{t('meta.supplyVoltage')}</label>
          <input
            id="m-supply"
            list="supply-options"
            value={m.supplyVoltage}
            onChange={(e) => set('supplyVoltage', e.target.value)}
          />
          <datalist id="supply-options">
            <option value="230 V" />
            <option value="3×230 V" />
            <option value="3N×400 V" />
          </datalist>
        </div>
        <div className="field">
          <label htmlFor="m-locale">{t('meta.locale')}</label>
          <select
            id="m-locale"
            value={m.locale}
            onChange={(e) => set('locale', e.target.value as ProjectMeta['locale'])}
            data-testid="meta-locale"
          >
            {(['fr', 'en', 'nl', 'de'] as const).map((l) => (
              <option key={l} value={l}>
                {LOCALES[l].label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="m-standard" title={t('meta.standardHint')}>
            {t('meta.standard')}
          </label>
          <select
            id="m-standard"
            value={m.standard}
            title={t('meta.standardHint')}
            onChange={(e) => set('standard', e.target.value as Standard)}
            data-testid="meta-standard"
          >
            {STANDARD_VALUES.map((v) => (
              <option key={v} value={v}>
                {t(`std.label.${v}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="m-usage" title={t('meta.usageHint')}>
            {t('meta.usage')}
          </label>
          <select
            id="m-usage"
            value={m.usage ?? 'domestic'}
            title={t('meta.usageHint')}
            onChange={(e) => set('usage', e.target.value as InstallationUsage)}
            data-testid="meta-usage"
          >
            <option value="domestic">{t('meta.usageDomestic')}</option>
            <option value="non-domestic">{t('meta.usageNonDomestic')}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="m-earth">{t('meta.earthingSystem')}</label>
          <select
            id="m-earth"
            value={m.earthingSystem}
            onChange={(e) => set('earthingSystem', e.target.value as EarthingSystem)}
          >
            {(['TT', 'TN-S', 'TN-C-S', 'IT'] as const).map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="m-icc" title={t('info.iccHint')}>
            {t('info.icc')}
          </label>
          <input
            id="m-icc"
            type="number"
            min={0}
            step={0.5}
            value={m.iccOriginKa ?? ''}
            title={t('info.iccHint')}
            onChange={(e) => set('iccOriginKa', e.target.value === '' ? undefined : Number(e.target.value))}
          />
        </div>
        <button type="submit" hidden />
      </form>
      <p className="muted small">{t('meta.hint')}</p>
    </Dialog>
  );
}
